// src/features/home/components/CategorySummaryList.jsx
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import {
  Box, Chip, Collapse, IconButton, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Category as CategoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { tasksAPI } from '../../tasks/services/tasks';
import TaskRow from './TaskRow';
import HomeCard from './HomeCard';

function CategoryRow({ category, t }) {
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && tasks === null) {
      setLoading(true);
      try {
        const result = await tasksAPI.getCategoryTasks(category.category_id);
        setTasks(result?.data?.data?.tasks || []);
      } finally {
        setLoading(false);
      }
    }
  }, [open, tasks, category.category_id]);

  return (
    <Box
      sx={{
        borderRadius: 5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        onClick={toggle}
        sx={{
          display: 'grid',
          gridTemplateColumns: '38px minmax(0, 1fr) auto 32px',
          alignItems: 'center',
          gap: 3,
          py: 3,
          px: 4,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: category.color_code ? alpha(category.color_code, 0.15) : 'action.selected',
            color: category.color_code || 'text.secondary',
          }}
        >
          <CategoryIcon sx={{ fontSize: 26 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} noWrap>{category.name}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {t('home:open_tasks.task_count', { count: category.total_open_count })}
            {category.description && ` · ${category.description}`}
          </Typography>
        </Box>

        {/* Only non-zero buckets, most urgent first */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {category.overdue_count > 0 && (
            <Chip size="small" variant="outlined" color="error" label={`${category.overdue_count} ${t('home:open_tasks.overdue')}`} />
          )}
          {category.due_today_count > 0 && (
            <Chip size="small" variant="outlined" color="warning" label={`${category.due_today_count} ${t('home:open_tasks.due_today')}`} />
          )}
          {category.later_count > 0 && (
            <Chip size="small" variant="outlined" label={`${category.later_count} ${t('home:open_tasks.later')}`} sx={{ color: 'text.secondary', borderColor: 'divider', bgcolor: 'transparent' }} />
          )}
        </Box>

        <IconButton size="small">
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 5, pb: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={18} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 0 }}>{t('tasks:data_grid.columns.deadline')}</TableCell>
                  <TableCell>{t('tasks:data_grid.columns.title')}</TableCell>
                  <TableCell>{t('tasks:data_grid.columns.due')}</TableCell>
                  <TableCell>{t('tasks:data_grid.columns.status')}</TableCell>
                  <TableCell sx={{ pr: 0, width: 32 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {(tasks || []).map((task) => (
                  <TaskRow
                    key={task.task_id}
                    task={task}
                    showStatusOnlyIfDue
                    badge={task.chain_open_count > 1 && (
                      <Tooltip title={t('home:open_tasks.chain_count_tooltip', { count: task.chain_open_count })}>
                        <Chip
                          icon={<WarningAmberIcon />}
                          label={task.chain_open_count}
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      </Tooltip>
                    )}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function CategorySummaryList({ categories, loading }) {
  const { t } = useTranslation(['home', 'tasks']);

  return (
    <HomeCard
      large
      icon={<CategoryIcon sx={{ fontSize: 22 }} />}
      title={t('home:open_tasks.title')}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={20} />
        </Box>
      ) : categories.length === 0 ? (
        <Typography variant="body2" color="text.tertiary" fontStyle="italic" sx={{ py: 2 }}>
          {t('home:open_tasks.empty')}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {
            categories.map((category) => (
              <CategoryRow key={category.category_id} category={category} t={t} />
            ))
          }
        </Box>
      )}
    </HomeCard>
  );
}
